import React, { createContext, useContext, ReactNode } from "react";
import type { ActivityEventKind } from "./sources/providers/types";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";

interface ActivitiesContextValue {
    workspaceId: string;
    userId: string;
    database: DatabaseAdapter | null;
    kinds?: ActivityEventKind[];
}

const ActivitiesContext = createContext<ActivitiesContextValue>({
    workspaceId: "",
    userId: "",
    database: null
});

/**
 * Provides workspace/user identity and a database adapter to all descendant
 * useWorkspaceActivities() hooks.
 *
 * Each app wires this once at the root:
 * - Core app: reads user.uid + workspace from globalStore
 * - Rapid: reads from useAuth() + useCurrentWorkspace()
 *
 * Both pass the DatabaseAdapter from their provider stack, which handles
 * the compat vs modular Firebase SDK difference.
 */
export function ActivitiesProvider({
    workspaceId,
    userId,
    database,
    kinds,
    children
}: {
    workspaceId: string;
    userId: string;
    database: DatabaseAdapter | null;
    kinds?: ActivityEventKind[];
    children: ReactNode;
}) {
    return (
        <ActivitiesContext.Provider value={{ workspaceId, userId, database, kinds }}>
            {children}
        </ActivitiesContext.Provider>
    );
}

export function useActivitiesContext(): ActivitiesContextValue {
    return useContext(ActivitiesContext);
}
